import { useTranslation } from 'react-i18next'
import { Check, Loader2, X } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventIdSurveyResponses } from '@/api/generated/company-management-user/company-management-user'
import type { CalendarEventSurveyAnswer } from '@/api/model/calendarEventSurveyAnswer'
import { SurveyEventAnswer } from '@/api/model/surveyEventAnswer'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'

export interface SurveyFields {
  surveyAppointmentAllowed: boolean
  capacity: number | null
  surveyAppointmentReward: number | null
}

interface Props {
  serviceModuleId: number
  /** Still undefined on create — then there are no answers to load. */
  eventId?: number
  value: SurveyFields
  onChange: (patch: Partial<SurveyFields>) => void
}

/**
 * Appointment survey fields (only visible when the module has survey subtypes):
 * allow survey + capacity/reward, plus the answers already submitted
 * (only in the edit case, since new appointments don't have an id yet).
 */
export function EventSurveyDetails({ serviceModuleId, eventId, value, onChange }: Props) {
  const { t } = useTranslation('service-modules-calendar')

  const num = (raw: string): number | null => (raw === '' ? null : Number(raw))

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">{t('event.survey.title')}</p>

      <div className="flex items-center gap-2">
        <Checkbox
          id="survey-allowed"
          checked={value.surveyAppointmentAllowed}
          onCheckedChange={(v) => onChange({ surveyAppointmentAllowed: v === true })}
        />
        <Label htmlFor="survey-allowed" className="font-normal">
          {t('event.survey.allowed')}
        </Label>
      </div>

      {value.surveyAppointmentAllowed && (
        <>
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="survey-capacity">{t('event.survey.capacity')}</Label>
              <Input
                id="survey-capacity"
                type="number"
                value={value.capacity ?? ''}
                onChange={(e) => onChange({ capacity: num(e.target.value) })}
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="survey-reward">{t('event.survey.reward')}</Label>
              <Input
                id="survey-reward"
                type="number"
                value={value.surveyAppointmentReward ?? ''}
                onChange={(e) => onChange({ surveyAppointmentReward: num(e.target.value) })}
              />
            </div>
          </div>

          {eventId != null && <SurveyResponses serviceModuleId={serviceModuleId} eventId={eventId} />}
        </>
      )}
    </div>
  )
}

function SurveyResponses({ serviceModuleId, eventId }: { serviceModuleId: number; eventId: number }) {
  const { t } = useTranslation('service-modules-calendar')
  const { data, isLoading } =
    useGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventIdSurveyResponses(
      serviceModuleId,
      eventId,
    )
  const responses = (data?.data ?? []) as CalendarEventSurveyAnswer[]

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t('event.survey.responsesTitle')}</p>
      {isLoading ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : responses.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('event.survey.responsesEmpty')}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {responses.map((r, i) => (
            <SurveyResponseRow key={`${r.employee?.firstName}-${r.selectionTimestamp}-${i}`} answer={r} />
          ))}
        </div>
      )}
    </div>
  )
}

function SurveyResponseRow({ answer }: { answer: CalendarEventSurveyAnswer }) {
  const { t, i18n } = useTranslation('service-modules-calendar')
  const accepted = answer.answer === SurveyEventAnswer.Accepted
  // Backend returns the time without a zone → interpret it as UTC.
  const when = new Date(`${answer.selectionTimestamp}Z`)
  const dateStr = when.toLocaleString(i18n.resolvedLanguage, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })

  return (
    <div className="flex items-center gap-2">
      {accepted ? (
        <Check className="size-4 shrink-0 text-emerald-700" />
      ) : (
        <X className="size-4 shrink-0 text-red-700" />
      )}
      <div className="flex flex-col">
        <span className="text-sm">
          {answer.employee?.firstName} {answer.employee?.lastName}
        </span>
        <span className={accepted ? 'text-xs text-emerald-700' : 'text-xs text-red-700'}>
          {t(accepted ? 'event.survey.accepted' : 'event.survey.declined', { date: dateStr })}
        </span>
      </div>
    </div>
  )
}
